/**
 * Восстановлено из бандла один в один: ключ rt-theme, синхронизация
 * meta[name=theme-color] и горячая клавиша Ctrl/Cmd + Shift + L.
 */
import { useSyncExternalStore, useEffect } from 'react';

const KEY = 'rt-theme';
const listeners = new Set<() => void>();

export type Theme = 'light' | 'dark';

export function current(): Theme {
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

export function apply(theme: Theme) {
  document.documentElement.setAttribute('data-theme', theme);
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // приватный режим — тема просто не запомнится
  }
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'dark' ? '#171615' : '#f3f2f2');

  for (const fn of listeners) fn();
}

export function toggle() {
  apply(current() === 'dark' ? 'light' : 'dark');
}

/** Применяется до первой отрисовки, чтобы не мигать светлой темой. */
export function restore() {
  const saved = (() => {
    try {
      return localStorage.getItem(KEY) as Theme | null;
    } catch {
      return null;
    }
  })();
  apply(saved ?? (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'));
}

export function useTheme(): Theme {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    current,
    () => 'light',
  );
}

export function useThemeHotkey() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.code === 'KeyL') {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
